/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2020 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2020 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { dateUtils } from '@opengeoweb/shared';
import type { CoreAppStore } from '@opengeoweb/store';
import {
  mapActions,
  mapSelectors,
  mapEnums,
  syncGroupsSelectors,
} from '@opengeoweb/store';
import {
  activeEditUnit,
  anchorInstantFor,
  AnimationLengthMode,
  isAnchoredMode,
  snapInstantToOffsetGrid,
  TimeSliderLegend,
} from '@opengeoweb/time-slider';

interface TimeSliderLegendConnectProps {
  mapId: string;
  onSetTimeSliderSpan?: (newSpan: number, newCenterTime: number) => void;
  unfilteredSelectedTime: number;
  setUnfilteredSelectedTime: (unfilteredSelectedTime: number) => void;
  mapWindowRef?: React.RefObject<HTMLElement | null>;
  adjustSelectedTimeOnWheel?: ({
    event,
    deltaY,
  }: {
    event: WheelEvent | KeyboardEvent;
    deltaY: number;
  }) => void;
}

export const TimeSliderLegendConnect: React.FC<
  TimeSliderLegendConnectProps
> = ({
  mapId,
  onSetTimeSliderSpan,
  unfilteredSelectedTime,
  setUnfilteredSelectedTime,
  mapWindowRef,
  adjustSelectedTimeOnWheel,
}) => {
  const isTimeScrollingEnabled = useSelector(
    syncGroupsSelectors.isTimeScrollingEnabled,
  );

  const centerTime = useSelector((store: CoreAppStore) =>
    mapSelectors.getMapTimeSliderCenterTime(store, mapId),
  );
  const timeSliderWidth = useSelector((store: CoreAppStore) =>
    mapSelectors.getMapTimeSliderWidth(store, mapId),
  );
  const secondsPerPx = useSelector((store: CoreAppStore) =>
    mapSelectors.getMapTimeSliderSecondsPerPx(store, mapId),
  );

  const timeStep = useSelector((store: CoreAppStore) =>
    mapSelectors.getMapTimeStep(store, mapId),
  );
  const isTimeSliderHoverOn = useSelector((store: CoreAppStore) =>
    mapSelectors.isTimeSliderHoverOn(store, mapId),
  );

  const { animationStartTime, animationEndTime } = useSelector(
    (store: CoreAppStore) => mapSelectors.getAnimationRange(store, mapId),
  );

  const timeSliderSpan = useSelector((store: CoreAppStore) =>
    mapSelectors.getMapTimeSliderSpan(store, mapId),
  );

  const animationLength = useSelector((store: CoreAppStore) =>
    mapSelectors.getAnimationLength(store, mapId),
  );

  const anchorInputs = useSelector((store: CoreAppStore) =>
    mapSelectors.getAnimationAnchorInputs(store, mapId),
  );

  /* ReferenceTime counts its offsets from the layer, so the reducer is told where that is. */
  const referenceTimeFromLayer = anchorInputs.referenceTime;

  /* An anchored mode counts whole units from its anchor, so a handle may only land on those. */
  const snapAnimationTime = React.useMemo(() => {
    if (!animationLength || !isAnchoredMode(animationLength.mode)) {
      return undefined;
    }
    const unit = activeEditUnit(animationLength);
    return (timeUnix: number): number =>
      dateUtils.unix(
        snapInstantToOffsetGrid(
          anchorInstantFor(animationLength, anchorInputs),
          dateUtils.fromUnix(timeUnix),
          unit,
        ),
      );
  }, [animationLength, anchorInputs]);

  const currentTime = dateUtils.unix(Date.now());

  const [dataStartTime, dataEndTime] = useSelector((store: CoreAppStore) =>
    mapSelectors.getDataLimitsFromLayers(store, mapId),
  );
  const dispatch = useDispatch();
  const [isDraggingStartAnimation, setIsDraggingStartAnimation] =
    React.useState(false);
  const [isDraggingEndAnimation, setIsDraggingEndAnimation] =
    React.useState(false);

  function convertStringTimeToUnix(
    time: string | undefined,
  ): number | undefined {
    return time ? dateUtils.unix(new Date(time)) : undefined;
  }

  const handleStartTimeChangeInLegend = (startTime: number): void => {
    const newTime = convertStringTimeToUnix(animationStartTime);
    if (newTime !== startTime) {
      dispatch(
        mapActions.setAnimationStartTime({
          mapId,
          animationStartTime: dateUtils.fromUnix(startTime).toISOString(),
          referenceTime: referenceTimeFromLayer,
          origin: mapEnums.MapActionOrigin.map,
        }),
      );
    }

    setIsDraggingStartAnimation(true);
  };

  const handleEndTimeChangeInLegend = (endTime: number): void => {
    const newTime = convertStringTimeToUnix(animationEndTime);
    if (newTime !== endTime) {
      dispatch(
        mapActions.setAnimationEndTime({
          mapId,
          animationEndTime: dateUtils.fromUnix(endTime).toISOString(),
          referenceTime: referenceTimeFromLayer,
          origin: mapEnums.MapActionOrigin.map,
        }),
      );
    }

    setIsDraggingEndAnimation(true);
  };

  return (
    <TimeSliderLegend
      adjustSelectedTimeOnWheel={adjustSelectedTimeOnWheel}
      centerTime={centerTime!}
      currentTime={currentTime}
      dataEndTime={dataEndTime}
      dataStartTime={dataStartTime}
      isAnimationRangeDraggable={
        animationLength?.mode !== AnimationLengthMode.Auto
      }
      isDraggingEndAnimation={isDraggingEndAnimation}
      isDraggingStartAnimation={isDraggingStartAnimation}
      isTimeScrollingEnabled={isTimeScrollingEnabled}
      isTimeSliderHoverOn={isTimeSliderHoverOn}
      mapId={mapId}
      mapWindowRef={mapWindowRef}
      onEndTimeChange={handleEndTimeChangeInLegend}
      onSetAnimationEndTime={(animationEndTime: string): void => {
        dispatch(
          mapActions.setAnimationEndTime({
            mapId,
            animationEndTime,
            referenceTime: referenceTimeFromLayer,
            origin: mapEnums.MapActionOrigin.map,
          }),
        );
      }}
      onSetAnimationStartTime={(animationStartTime: string): void => {
        dispatch(
          mapActions.setAnimationStartTime({
            mapId,
            animationStartTime,
            referenceTime: referenceTimeFromLayer,
            origin: mapEnums.MapActionOrigin.map,
          }),
        );
      }}
      onSetCenterTime={(newTime: number): void => {
        dispatch(
          mapActions.setTimeSliderCenterTime({
            mapId,
            timeSliderCenterTime: newTime,
          }),
        );
      }}
      onSetTimeSliderSpan={onSetTimeSliderSpan}
      onStartTimeChange={handleStartTimeChangeInLegend}
      reduxAnimationEndTime={animationEndTime}
      reduxAnimationStartTime={animationStartTime}
      secondsPerPx={secondsPerPx!}
      setIsDraggingEndAnimation={setIsDraggingEndAnimation}
      setIsDraggingStartAnimation={setIsDraggingStartAnimation}
      setUnfilteredSelectedTime={setUnfilteredSelectedTime}
      snapAnimationTime={snapAnimationTime}
      timeSliderSpan={timeSliderSpan}
      timeSliderWidth={timeSliderWidth!}
      timeStep={timeStep}
      unfilteredSelectedTime={unfilteredSelectedTime}
      updateCanvasWidth={(storeWidth: number, newWidth: number): void => {
        if (storeWidth !== newWidth) {
          dispatch(
            mapActions.setTimeSliderWidth({
              mapId,
              timeSliderWidth: newWidth,
            }),
          );
        }
      }}
    />
  );
};
